import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProducts,
  fetchCategories,
  fetchProductById,
  fetchDeliveryTypes,
  submitOrder,
  normaliseProduct,
  normaliseCategory,
  normaliseDeliveryType,
  normaliseOrder,
  sendOtp,
  verifyOtp,
  normaliseCustomer,
  fetchCustomer,
  updateCustomer,
  fetchCustomerOrders,
  fetchCustomerOrderById,
  fetchBanners,
  normaliseBanner,
  fetchClients,
  normaliseClient,
  fetchReels,
  normaliseReel,
  verifyPayment,
  markPaymentFailed,
  fetchCategoryTree
} from '../api';

// ── All products (normalised, active only) ─────────────────
export const useProducts = () =>
  useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const raw = await fetchProducts();
      return raw.filter((p) => p.is_active && hasActiveCategory(p)).map(normaliseProduct);
    },
    staleTime: 1000 * 60 * 5,
  });

// ── Bestsellers ────────────────────────────────────────────
export const useBestsellers = () =>
  useQuery({
    queryKey: ['products', 'bestsellers'],
    queryFn: async () => {
      const raw = await fetchProducts();
      return raw.filter((p) => p.is_active && p.is_featured && hasActiveCategory(p)).map(normaliseProduct);
    },
    staleTime: 1000 * 60 * 5,
  });

// ── New Arrivals ───────────────────────────────────────────
export const useNewArrivals = () =>
  useQuery({
    queryKey: ['products', 'new-arrivals'],
    queryFn: async () => {
      const raw = await fetchProducts();
      return raw.filter((p) => p.is_active && p.is_new_arrival && hasActiveCategory(p)).map(normaliseProduct);
    },
    staleTime: 1000 * 60 * 5,
  });

// ── Single product by id ───────────────────────────────────
export const useProduct = (id) =>
  useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      try {
        const raw = await fetchProductById(id);
        return normaliseProduct(raw);
      } catch {
        const all = await fetchProducts();
        const found = all.find((p) => String(p.id) === String(id));
        return found ? normaliseProduct(found) : null;
      }
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
  });

// ── Categories ─────────────────────────────────────────────
export const useCategories = () =>
  useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const raw = await fetchCategories();
      return raw.filter((c) => c.is_active).map(normaliseCategory);
    },
    staleTime: 1000 * 60 * 10,
  });

// ── Products by category ───────────────────────────────────
export const useProductsByCategory = (slug) =>
  useQuery({
    queryKey: ['products', 'category', slug],
    queryFn: async () => {
      const raw = await fetchProducts();
      return raw
        .filter((p) => p.is_active && hasActiveCategory(p) && (!slug || p.category?.slug === slug))
        .map(normaliseProduct);
    },
    staleTime: 1000 * 60 * 5,
  });

// ── Delivery Types ─────────────────────────────────────────
export const useDeliveryTypes = () =>
  useQuery({
    queryKey: ['delivery-types'],
    queryFn: async () => {
      const raw = await fetchDeliveryTypes();
      return raw.filter((d) => d.is_active !== false).map(normaliseDeliveryType);
    },
    staleTime: 1000 * 60 * 10,
  });

// ── Submit Order ───────────────────────────────────────────
export const useSubmitOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const raw = await submitOrder(payload);
      return normaliseOrder(raw);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// ── Customer Auth Mutations ───────────────────────────────────
// ─────────────────────────────────────────────────────────────

// useSendOtp — POST /api/auth/send-otp
export const useSendOtp = () =>
  useMutation({
    mutationFn: (email) => sendOtp(email),
  });

// useVerifyOtp — POST /api/auth/verify-otp
export const useVerifyOtp = () =>
  useMutation({
    mutationFn: ({ email, otp }) => verifyOtp(email, otp),
    onSuccess: (data) => {
      if (data?.customer) {
        const normalised = normaliseCustomer(data.customer);
        localStorage.setItem('customer', JSON.stringify(normalised));
      }
    },
  });

// ─────────────────────────────────────────────────────────────
// ── Customer Profile ──────────────────────────────────────────
// ─────────────────────────────────────────────────────────────

/**
 * useCustomer — GET /api/customer/:id
 * Fetches latest customer data from the server.
 * Pass the customer ID from localStorage.
 */
export const useCustomer = (id) =>
  useQuery({
    queryKey: ['customer', id],
    queryFn: () => fetchCustomer(id),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
  });

/**
 * useUpdateCustomer — PUT /api/customer/:id
 * Updates first_name, last_name, phone.
 * On success, refreshes the customer query and syncs localStorage.
 */
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCustomer,
    onSuccess: (updatedCustomer) => {
      // Refresh the customer query so data stays fresh
      queryClient.invalidateQueries({ queryKey: ['customer', updatedCustomer.id] });

      // Keep localStorage in sync so Navbar avatar/name updates immediately
      try {
        const stored = JSON.parse(localStorage.getItem('customer') || '{}');
        const merged = {
          ...stored,
          firstName: updatedCustomer.first_name,
          lastName: updatedCustomer.last_name,
          phone: updatedCustomer.phone,
          fullName: `${updatedCustomer.first_name ?? ''} ${updatedCustomer.last_name ?? ''}`.trim(),
        };
        localStorage.setItem('customer', JSON.stringify(merged));
      } catch {
        // silent
      }
    },
  });
};

// ── Customer Orders ────────────────────────────────────────

export const useCustomerOrders = (customerId) =>
  useQuery({
    queryKey: ['customer-orders', customerId],
    queryFn: () => fetchCustomerOrders(customerId),
    enabled: Boolean(customerId),
    staleTime: 1000 * 60 * 2,
  });

export const useCustomerOrderDetail = (customerId, orderId) =>
  useQuery({
    queryKey: ['customer-order', customerId, orderId],
    queryFn: () => fetchCustomerOrderById(customerId, orderId),
    enabled: Boolean(customerId) && Boolean(orderId),
    staleTime: 1000 * 60 * 2,
  });

// ── Banners ────────────────────────────────────────────────
export const useBanners = () =>
  useQuery({
    queryKey: ['banners'],
    queryFn: async () => {
      const raw = await fetchBanners();
      return raw
        .filter((b) => b.is_active !== false)
        .map(normaliseBanner)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
    staleTime: 1000 * 60 * 10,
  });

// ── Trusted Clients ────────────────────────────────────────
export const useClients = () =>
  useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const raw = await fetchClients();
      return raw
        .filter((c) => c.is_active !== false)
        .map(normaliseClient)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
    staleTime: 1000 * 60 * 10,
  });

// ── Reels insta ────────────────────────────────────────
export const useReels = () =>
  useQuery({
    queryKey: ['reels'],
    queryFn: async () => {
      const raw = await fetchReels();
      return raw
        .filter((c) => c.is_active !== false)
        .map(normaliseReel)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
    staleTime: 1000 * 60 * 10,
  });

//Verified payment
export const useVerifyPayment = () =>
  useMutation({
    mutationFn: (payload) => verifyPayment(payload),
  });

export const useMarkPaymentFailed = () =>
  useMutation({
    mutationFn: (payload) => markPaymentFailed(payload),
  });

// ── NEW ──
// A product's `categories[]` (or a category node's own is_active) can be
// inactive even when the product itself is active — hide those from every
// listing that reads from useProducts/useCategoryTree.
const hasActiveCategory = (p) => (p.categories || []).some((c) => Boolean(c.is_active));

const filterActiveCategoryTree = (nodes = []) =>
  nodes
    .filter((n) => Boolean(n.is_active))
    .map((n) => ({ ...n, children: filterActiveCategoryTree(n.children || []) }));

export const useCategoryTree = () =>
  useQuery({
    queryKey: ['categories', 'tree'],
    queryFn: async () => {
      const raw = await fetchCategoryTree();
      return filterActiveCategoryTree(raw);
    },
    staleTime: 1000 * 60 * 10,
  });